import React, { useEffect } from "react";

import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@components/buttons";
import {
	OnboardingRegistrationBirthName,
	OnboardingRegistrationCity,
	OnboardingRegistrationConditions,
	OnboardingRegistrationHouseNumber,
	OnboardingRegistrationLanguage,
	OnboardingRegistrationMonthlyLimit,
	OnboardingRegistrationNationality,
	OnboardingRegistrationPOB,
	OnboardingRegistrationPhone,
	OnboardingRegistrationPromotion,
	OnboardingRegistrationState,
	OnboardingRegistrationStreet,
	OnboardingRegistrationZIPCode,
} from "@features/membership/desktop/registration-types/onboarding-registration";
import RegistrationInputErrorWrapper from "@features/membership/shared/registration-types/shared/RegistrationInputErrorWrapper";
import { useRegistrationStore } from "@state/context/membership/registration-types/RegistrationStoreContext";
import { useAppContext } from "@state/hooks/shared";
import { getCurrentCulture } from "@utils";
import { isRegistrationDomain } from "@utils/membership/membershipUtils";
import { observer } from "mobx-react";

export default observer(function OnboardingRegistrationContactDetails() {
	const {
		formState: { isSubmitting },
	} = useFormContext();

	const { culture } = useAppContext();
	const { t } = useTranslation("registration");

	const {
		previousStep,
		countryData,
		languageData,
		gamingLimitsData,
		countryRegionsData,
	} = useRegistrationStore();

	useEffect(() => {
		//on mount scroll to top
		document.querySelector("html, body")?.scrollTo(0, 0);
	}, []);

	return (
		<>
			<div className="card--membership__form">
				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_street"}
				>
					<OnboardingRegistrationStreet labelClassName="label--strong" />
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_housenumber"}
				>
					<OnboardingRegistrationHouseNumber labelClassName="label--strong" />
				</RegistrationInputErrorWrapper>

				<div className="form__field form--grid u-mar--bottom--reset">
					<div className="form--grid__row row">
						<div className="form--grid__col col col-sml-3">
							<RegistrationInputErrorWrapper
								componentName={
									"onboarding-registration_zipcode"
								}
							>
								<OnboardingRegistrationZIPCode labelClassName="label--strong" />
							</RegistrationInputErrorWrapper>
						</div>
						<div className="form--grid__col col col-sml-9">
							<RegistrationInputErrorWrapper
								componentName={"onboarding-registration_city"}
							>
								<OnboardingRegistrationCity labelClassName="label--strong" />
							</RegistrationInputErrorWrapper>
						</div>
					</div>
				</div>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_state"}
				>
					<OnboardingRegistrationState
						countryRegionsData={countryRegionsData}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_nationality"}
				>
					<OnboardingRegistrationNationality
						countryData={countryData}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_birthname"}
				>
					<OnboardingRegistrationBirthName labelClassName="label--strong" />
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_placeofbirth"}
				>
					<OnboardingRegistrationPOB labelClassName="label--strong" />
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_phone"}
				>
					<OnboardingRegistrationPhone inputClassName="PhoneInput--sml" />
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_monhtlylimit"}
				>
					<OnboardingRegistrationMonthlyLimit
						gamingLimitsData={gamingLimitsData}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_language"}
				>
					<OnboardingRegistrationLanguage
						languageData={languageData}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_promotion"}
				>
					<OnboardingRegistrationPromotion />
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_conditions"}
				>
					<OnboardingRegistrationConditions />
				</RegistrationInputErrorWrapper>
			</div>

			<div className="form__field row">
				<div className="col col-sml-6">
					<Button
						type="button"
						atClassName="regAccountBtnPrev"
						className="btn--base btn--block btn--primary--inverted"
						iconLeft
						iconClassName="u-icon--tny u-icon--arrow-left"
						btnText={t("COMMON.PREVIOUS_BUTTON")}
						onClick={previousStep}
					/>
				</div>
				<div className="col col-sml-6">
					<Button
						disabled={isSubmitting}
						atClassName="regAccountBtnNext"
						className="btn--base btn--block btn--accent"
						btnContent={<>{t<string>("OPEN_ACCOUNT")} *</>}
					/>
				</div>
			</div>

			<div className="card--membership__footer">
				{t("EXISTING_ACCOUNT")}
				{isRegistrationDomain() ? (
					<a
						href={`${WEBPACK_WEBSITE_URL}/${culture}/auth/login`}
						className="u-anchor u-anchor--primary u-anchor--underline"
					>
						&nbsp;{t("COMMON.LOGIN_BUTTON")}
					</a>
				) : (
					<Link
						to={`/${getCurrentCulture()}/auth/login`}
						className="u-anchor u-anchor--primary u-anchor--underline"
					>
						&nbsp;{t("COMMON.LOGIN_BUTTON")}
					</Link>
				)}
			</div>
		</>
	);
});
